import { isAnyOf } from '@reduxjs/toolkit';
import { pick } from 'ramda';

import { actions as cartActions } from '../cart/reducer';
import { actions as dashboardActions } from '../dashboard/reducer';
import {
  cancelOrder,
  lookupOrderDetails,
  lookupPosOrderDetails,
  resendPaymentEmail,
} from '../dashboard/thunks';
import { actions as guestActions } from '../guest/reducer';
import { AppStartListening } from '../listenerMiddleware';
import { actions as orderActions } from '../order/reducers';
import { selectDeliveryAddress } from '../order/selectors';
import { submitOrder, validateAddress } from '../order/thunks';
import { State } from '../reducers';
import { maskEmail, roundNumber } from '../util/utils';
import { DashboardOrder } from '../dashboard/schemas';
import { createSubmitOrderAnalytics } from './analytics';
import {
  logDeliveryAddressAddedEvent,
  logDeliveryAddressValidatedEvent,
  logDeliveryOrPickupSelectedEvent,
  logGuestAddedEvent,
  logMenuItemAddedEvent,
  logOrderCanceledEvent,
  logOrderEditOpenEvent,
  logOrderSubmittedEvent,
  logOrderViewedEvent,
  logOrdersExportedEvent,
  logPaymentMethodSelectedEvent,
  logPickupOrDeliveryTimeSetEvent,
  logResendCommunicationEvent,
} from './datadog';

const sanitizeGuestDetails = (guest: GuestDetails) => ({
  first: guest.first,
  last: guest.last,
  email: maskEmail(guest.email),
});

const getAddressDetailsFromState = (state: State) => ({
  ...pick(['addressLine1', 'addressLine2', 'city', 'state', 'zip'], state.order.deliveryAddress),
  ...selectDeliveryAddress(state),
});

const addDatadogListeners = (startListening: AppStartListening) => {
  startListening({
    actionCreator: orderActions.changeDestination,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;

      logDeliveryOrPickupSelectedEvent({
        orderId: state.order.id,
        option: action.payload.destination,
      });
    },
  });

  startListening({
    predicate: (action): action is ReturnType<typeof guestActions.setCMTCustomer> =>
      guestActions.setCMTCustomer.match(action) && !!action.payload.cfaId,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const cmtCustomer = action.payload;
      const guestDetails = {
        cfaId: cmtCustomer.cfaId,
        email: cmtCustomer.email,
        phone: cmtCustomer.phone,
        first: cmtCustomer.first ?? cmtCustomer.firstName,
        last: cmtCustomer.last ?? cmtCustomer.lastName,
      };

      logGuestAddedEvent({
        orderId: state.order.id,
        guestId: cmtCustomer.cfaId,
        guestDetails: sanitizeGuestDetails(guestDetails),
      });
    },
  });

  startListening({
    actionCreator: orderActions.setDetailsTime,
    effect: (action, listenerApi) => {
      const selectedTime = action.payload;

      if (!selectedTime) {
        return;
      }

      const state = listenerApi.getState() as State;

      logPickupOrDeliveryTimeSetEvent({
        orderId: state.order.id,
        selectedTime,
      });
    },
  });

  startListening({
    actionCreator: orderActions.autocompleteAddress,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const { address } = action.payload;

      logDeliveryAddressAddedEvent({
        orderId: state.order.id,
        addressDetails: {
          submittedAddress: address,
          ...pick(['addressLine1', 'addressLine2', 'city', 'state', 'zip'], state.order.deliveryAddress),
          ...selectDeliveryAddress(state),
        },
      });
    },
  });

  startListening({
    actionCreator: validateAddress.fulfilled,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const { address, response } = action.payload;

      logDeliveryAddressValidatedEvent({
        orderId: state.order.id,
        addressDetails: {
          submittedAddress: address,
          ...getAddressDetailsFromState(state),
        },
        validationResponse: {
          valid: true,
          response: pick(
            [
              'streetNumber',
              'route',
              'subpremise',
              'locality',
              'administrativeAreaLevelOne',
              'administrativeAreaLevelTwo',
              'postalCode',
              'country',
            ],
            response,
          ),
        },
      });
    },
  });

  startListening({
    actionCreator: validateAddress.rejected,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const submittedAddress =
        action.payload && typeof action.payload === 'object' && 'address' in action.payload
          ? action.payload.address
          : state.order.lastAddressSentForValidation;

      logDeliveryAddressValidatedEvent({
        orderId: state.order.id,
        addressDetails: {
          submittedAddress,
          ...getAddressDetailsFromState(state),
        },
        validationResponse: {
          valid: false,
          error: action.error.message,
        },
      });
    },
  });

  startListening({
    actionCreator: cartActions.addToCart,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const { id, itemTag, dessertTag, sideTag, premiumSideTag } = action.payload;

      logMenuItemAddedEvent({
        orderId: state.order.id,
        id,
        itemTag,
        dessertTag,
        sideTag,
        premiumSideTag,
      });
    },
  });

  startListening({
    actionCreator: orderActions.setPaymentMethod,
    effect: (action, listenerApi) => {
      const state = listenerApi.getState() as State;

      logPaymentMethodSelectedEvent({
        orderId: state.order.id,
        paymentMethod: action.payload,
      });
    },
  });

  startListening({
    actionCreator: submitOrder.fulfilled,
    effect: (action, listenerApi) => {
      const { getState } = listenerApi;
      const state = getState() as State;
      const analytics = createSubmitOrderAnalytics(state);
      const { payload } = action;

      logOrderSubmittedEvent({
        ...analytics,
        orderId: payload.previousOrderState.id,
        taxAmount: payload.previousOrderState.taxAmount,
        subTotalAmount: payload.previousOrderState.subTotalAmount,
        reorder: payload.previousOrderState.reorder,
        totalAmount: roundNumber(
          (payload?.previousOrderState?.taxAmount || 0) +
            (payload?.previousOrderState?.subTotalAmount || 0) +
            (payload?.previousOrderState?.deliveryTip?.tipAmount || 0),
        ),
        clientId: payload.order.clientId,
        isEditingOrder: undefined,
        destination: undefined,
        promoFreeItemCount: undefined,
        promoFreeItemsValue: undefined,
        sendCustomerNotifications: undefined,
      });
    },
  });

  startListening({
    matcher: isAnyOf(lookupOrderDetails.fulfilled, lookupPosOrderDetails.fulfilled),
    effect: (action) => {
      const { order } = action.payload as { order: DashboardOrder };
      logOrderViewedEvent({
        orderId: order.id,
      });
    },
  });

  startListening({
    actionCreator: orderActions.initiateEditOrder,
    effect: (action) => {
      // Past-order reorder also uses initiateEditOrder; only track dashboard edits.
      if (action.payload.reorder) {
        return;
      }

      logOrderEditOpenEvent({
        orderId: action.payload.order.id,
      });
    },
  });

  startListening({
    actionCreator: resendPaymentEmail.fulfilled,
    effect: (action) => {
      logResendCommunicationEvent({
        orderId: action.meta.arg.orderId,
        activeCommunication: 'payment',
      });
    },
  });

  startListening({
    actionCreator: cancelOrder.fulfilled,
    effect: (action) => {
      logOrderCanceledEvent({
        orderId: action.meta.arg.orderId,
      });
    },
  });

  startListening({
    actionCreator: dashboardActions.downloadExcel,
    effect: (_action, listenerApi) => {
      const state = listenerApi.getState() as State;
      const orderIds = state.dashboard.exportExcelOrders.map((order) => order.id).filter(Boolean);

      logOrdersExportedEvent({ orderIds });
    },
  });
};

export default addDatadogListeners;
